<template>
  <div class="cart-form">
    <alert-box v-for="(alert, i) in alerts" :key="i" :type="alert.type">
      {{ alert.message }}
    </alert-box>
    <template v-if="lineItems.length > 0">
      <div class="table-responsive">
        <table class="table">
          <thead>
            <tr class="active">
              <th class="cart-item-description-header">Item</th>
              <th class="cart-item-description-header hidden-xs" />
              <th class="cart-item-price-header hidden-xs">Price</th>
              <th class="cart-item-quantity-header">Qty</th>
              <th class="cart-item-total-header">Total</th>
              <th class="cart-item-delete-header" />
            </tr>
          </thead>
          <tbody>
            <line-item
              v-for="lineItem in lineItems"
              :key="lineItem.id"
              :line-item="lineItem"
              :disabled="submitting"
              @change="onLineItemChange"
            />
            <free-sample
              v-for="lineItem in freeSamples"
              :key="lineItem.id"
              :line-item="lineItem"
              @removed="removeFreeSample"
            />
            <tr v-if="showFreeSampleSelect" class="line-item">
              <td class="hidden-xs" />
              <td>
                <free-sample-select
                  :disabled="submitting"
                  @change="addFreeSample"
                />
              </td>
              <td colspan="4" />
            </tr>
            <tr
              v-else-if="freeSamples.length === 0"
              class="line-item free-sample-note"
            >
              <td class="hidden-xs" />
              <td colspan="4">
                <span class="text-success">
                  Orders with a subtotal over $25 qualify for a free fragrance
                  sample.
                </span>
              </td>
              <td class="hidden-xs" />
            </tr>
          </tbody>
          <promotion
            v-for="promotion in promotions"
            :key="promotion.id"
            :name="promotion.label"
            :amount="promotion.display_amount"
          />
          <tfoot>
            <tr class="warning cart-total">
              <td colspan="2" class="hidden-xs" />
              <td colspan="2" align="right">Order Subtotal</td>
              <td colspan="2" class="lead">{{ displayTotal }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div class="row">
        <div class="col-xs-6">
          <a :href="continueShoppingUrl" class="btn btn btn-light-green">
            Continue shopping
          </a>
          <empty-cart-button :url="emptyCartUrl">
            <button class="btn btn btn-danger" disabled>Empty Cart</button>
          </empty-cart-button>
        </div>
        <div class="col-xs-6 text-right">
          <template v-if="checkoutIsClosed">
            <disabled-checkout-button :disabled-message="closedCheckoutMessage" />
          </template>
          <form
            v-else
            class="checkout-form form-group"
            action="/checkout"
            method="POST"
            @submit="submitting = true"
          >
            <input type="hidden" name="authenticity_token" :value="csrfToken" />

            <button
              v-if="submitting"
              class="btn btn-secondary disabled checkout-button"
            >
              Loading ...
            </button>
            <button
              v-else
              type="submit"
              class="btn btn-light-green checkout-button"
            >
              Checkout
            </button>
          </form>
          <div class="item-weight">Item Weight {{ itemsWeight }}</div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="alert alert-info">Your cart is empty</div>
      <a :href="continueShoppingUrl" class="btn btn-primary btn-stretch">
        Continue shopping
      </a>
    </template>
  </div>
</template>

<script>
  import axios from "axios"
  import updateCart from "mixins/storefront/update-cart"
  import cartAlerts from "mixins/storefront/cart-alerts"
  import FreeSample from "./cart-form/free-sample"
  import Promotion from "./cart-form/promotion"
  import FreeSampleSelect from "./cart-form/free-sample-select"
  import LineItem from "./cart-form/line-item"
  import EmptyCartButton from "./cart-form/empty-cart-button"
  import DisabledCheckoutButton from "./cart-form/disabled-checkout-button"

  export default {
    components: {
      EmptyCartButton,
      FreeSample,
      FreeSampleSelect,
      LineItem,
      Promotion,
      DisabledCheckoutButton,
    },
    mixins: [updateCart, cartAlerts],
    props: {
      csrfToken: {
        type: String,
        required: true
      },
      url: {
        type: String,
        required: true
      },
      emptyCartUrl: {
        type: String,
        required: true
      },
      orderEligibleForFreeSample: {
        type: Boolean,
        default: false
      },
      initialLineItems: {
        type: Array,
        required: true
      },
      initialFreeSamples: {
        type: Array,
        default: () => []
      },
      initialPromotions: {
        type: Array,
        default: () => []
      },
      initialDisplayTotal: {
        type: String,
        required: true
      },
      initialCartWeight: {
        type: String,
        required: true
      },
      continueShoppingUrl: {
        type: String,
        required: true
      },
      checkoutIsClosed: {
        type: Boolean,
        default: false,
      },
      closedCheckoutMessage: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        lineItems: this.initialLineItems,
        freeSamples: this.initialFreeSamples,
        promotions: this.initialPromotions,
        displayTotal: this.initialDisplayTotal,
        itemsWeight: this.initialCartWeight,
        eligibleForFreeSample: this.orderEligibleForFreeSample,
        submitting: false
      }
    },
    computed: {
      showFreeSampleSelect() {
        return this.eligibleForFreeSample && this.freeSamples.length === 0
      }
    },
    methods: {
      onLineItemChange(lineItem) {
        const stateItem = this.lineItems.find((item) => {
          return item.id === lineItem.id
        })
        stateItem.quantity = lineItem.quantity
        this.submit()
      },
      submit() {
        this.alerts = []
        this.submitting = true
        axios.patch(this.url, {
          line_items: this.lineItems.map((lineItem) => {
            return {
              id: lineItem.id,
              quantity: lineItem.quantity,
              variant_id: lineItem.variant.id
            }
          })
        })
          .then((response) => {
            const data = response.data
            switch (response.status) {
              case 200:
                this.updateCart(data.order)
                this.lineItems = data.order.line_items
                this.freeSamples = data.order.free_samples
                this.promotions = data.order.promotions
                this.displayTotal = data.order.display_total
                this.itemsWeight = data.order.display_weight_for_cart
                this.eligibleForFreeSample = data.order.eligible_for_free_sample
                data.warnings.forEach((warning) => this.alert(warning))
                this.$emit("update")
                break
              case 422:
                response.data.errors.forEach((error) => {
                  this.alert(error, "danger")
                })
                break
              default:
                break
            }
          })
          .catch((error) => {
            if (error.response && error.response.data) {
              error.response.data.errors.forEach((message) => {
                this.alert(message, "danger")
              })
            } else {
              this.alert(error.message || error, "danger")
            }
          })
          .finally(() => {
            this.submitting = false
          })
      },
      addFreeSample(variantId) {
        this.submitting = true
        axios
          .post("/cs/api/free_sample", {
            variant_id: variantId
          })
          .then((response) => {
            this.freeSamples.push(response.data.line_item)
          })
          // eslint-disable-next-line no-alert
          .catch((error) => alert(error))
          .finally(() => {
            this.submitting = false
          })
      },
      removeFreeSample() {
        this.freeSamples = []
      }
    }
  }
</script>

<style lang="scss">
  @import "../../styles/globals/variables";

  .cart-form,
  .alert {
    margin-bottom: 1em;
  }

  #cart-form {
    td {
      font-size: 20px;
      font-weight: 300;
      color: $dark-blue-gray;
      vertical-align: middle;

      &:first-child {
        margin: 40px 0;
      }
    }

    .cart-total,
    .cart-subtotal {
      td:not(.lead) {
        font-size: 16px;
        font-weight: 700;
        padding-top: 1em;
        padding-bottom: 1em;
      }
    }
  }

  @media screen and (max-width: $screen-sm - 1) {
    #cart-form td {
      font-size: 17px;
    }
  }

  @media screen and (max-width: $screen-xs - 1) {
    #cart-form {
      .cart-item-description {
        font-size: 15px;
      }

      .btn {
        font-size: 14px;
      }
    }
  }
</style>
